import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Pencil, Check, Plus, RotateCcw, Layers, Loader2, ArrowRight, Monitor, Smartphone, Eye,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { WIDGETS, SIZE_PRESETS } from "@/lib/widgets";
import { cloneDefault } from "@/lib/dashboardDefault";
import { DashboardGrid } from "@/components/dashboard/DashboardGrid";
import { WidgetLibrary } from "@/components/dashboard/WidgetLibrary";
import { WidgetConfig } from "@/components/dashboard/WidgetConfig";
import { TemplateManager } from "@/components/dashboard/TemplateManager";

export default function Dashboard() {
  const [layout, setLayout] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [editing, setEditing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [configId, setConfigId] = useState(null);
  const [previewRole, setPreviewRole] = useState("");
  const [roles, setRoles] = useState([]);
  const savedLayout = useRef(null);

  // Admin: preview a role's Starter Dashboard (read-only) without changing yours.
  const loadRolePreview = useCallback(async (role) => {
    if (!role) {
      if (savedLayout.current) { setLayout(savedLayout.current); savedLayout.current = null; }
      setPreviewRole(""); setEditing(false); return;
    }
    try {
      const t = await apiFetch(`/dashboard-templates/assigned?role=${encodeURIComponent(role)}`);
      if (!t?.layout?.widgets) return toast.error("No starter dashboard for that role yet.");
      if (!savedLayout.current) savedLayout.current = layout;
      setPreviewRole(role); setEditing(false);
      setLayout({ ...t.layout, source: "role-preview" });
    } catch (e) { toast.error(e.message); }
  }, [layout]);

  const saveRoleStarter = async () => {
    try {
      await apiFetch("/dashboard-templates/role-starter", {
        method: "PUT", body: JSON.stringify({ role_name: previewRole, layout }) });
      toast.success(`Saved as ${previewRole} starter dashboard`);
    } catch (e) { toast.error(e.message); }
  };

  // initial load: personal layout -> role template -> Nirvana Default
  useEffect(() => {
    (async () => {
      try {
        const [prefs, dash, appr] = await Promise.all([
          apiFetch("/preferences"), apiFetch("/dashboard"), apiFetch("/appearance"),
        ]);
        setGreeting(dash?.greeting_name || "");
        setIsAdmin(!!appr?.is_admin);
        if (appr?.is_admin) { try { setRoles(await apiFetch("/roles")); } catch { /* ignore */ } }
        if (prefs?.dashboard?.widgets) {
          setLayout(prefs.dashboard);
        } else {
          // Resolution: personal → role starter → organisation default → system default
          let base = null;
          try { base = await apiFetch("/dashboard-templates/assigned"); } catch { /* none */ }
          if (!base?.layout?.widgets) {
            try { base = await apiFetch("/dashboard-templates/org-default"); } catch { /* none */ }
          }
          const src = base?.scope === "org" ? "org" : (base?.scope === "role" ? "role" : "default");
          setLayout(base?.layout?.widgets ? { ...base.layout, source: src } : cloneDefault());
        }
      } catch (e) {
        toast.error(e.message || "Could not load dashboard.");
        setLayout(cloneDefault());
      }
    })();
  }, []);

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      await apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { dashboard: next } }) });
      setDirty(false);
      toast.success("Dashboard saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  }, []);

  const firstLayoutEmit = useRef(true);
  const onLayoutChange = useCallback((all) => {
    if (!editing) return;
    if (firstLayoutEmit.current) { firstLayoutEmit.current = false; return; }
    setLayout((l) => {
      const byId = (arr) => Object.fromEntries((arr || []).map((x) => [x.i, x]));
      const dLg = byId(all.lg), dXs = byId(all.xs);
      return {
        ...l,
        desktop: (l.desktop || []).map((it) => dLg[it.i] ? { ...it, ...dLg[it.i] } : it),
        mobile: (l.mobile || []).map((it) => dXs[it.i] ? { ...it, ...dXs[it.i] } : it),
        source: "personal",
      };
    });
    setDirty(true);
  }, [editing]);

  const mutate = useCallback((fn) => { setLayout((l) => { const n = fn(structuredClone(l)); n.source = "personal"; return n; }); setDirty(true); }, []);

  const onCollapse = (id) => mutate((l) => { l.widgets[id].collapsed = !l.widgets[id].collapsed; return l; });
  const onHide = (id) => mutate((l) => { l.widgets[id].hidden = true; return l; });
  const onResize = (id, size) => mutate((l) => {
    l.widgets[id].size = size;
    const p = SIZE_PRESETS[size];
    l.desktop = l.desktop.map((it) => it.i === id ? { ...it, w: p.w, h: p.h } : it);
    l.mobile = l.mobile.map((it) => it.i === id ? { ...it, w: p.mW, h: p.mH } : it);
    return l;
  });
  const onSaveConfig = (cfg) => { mutate((l) => { l.widgets[configId].config = cfg; return l; }); setConfigId(null); };

  const addWidget = (type) => mutate((l) => {
    if (l.widgets[type]) { l.widgets[type].hidden = false; return l; }
    const w = WIDGETS[type];
    const p = SIZE_PRESETS[w.size];
    l.widgets[type] = { type, config: {}, collapsed: false, hidden: false, size: w.size };
    const maxY = Math.max(0, ...l.desktop.map((it) => it.y + it.h));
    const maxYm = Math.max(0, ...l.mobile.map((it) => it.y + it.h));
    l.desktop.push({ i: type, x: 0, y: maxY, w: p.w, h: p.h });
    l.mobile.push({ i: type, x: 0, y: maxYm, w: 4, h: p.mH });
    return l;
  });

  // Re-resolve the base layout down the chain (role starter → org default → system).
  const resolveBase = useCallback(async () => {
    let base = null;
    try { base = await apiFetch("/dashboard-templates/assigned"); } catch { /* none */ }
    if (!base?.layout?.widgets) { try { base = await apiFetch("/dashboard-templates/org-default"); } catch { /* none */ } }
    const src = base?.scope === "org" ? "org" : (base?.scope === "role" ? "role" : "default");
    return base?.layout?.widgets ? { ...base.layout, source: src } : cloneDefault();
  }, []);

  const resetDefault = async () => {
    // Clear the personal override so the resolution chain applies again.
    await apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { dashboard: null } }) });
    const base = await resolveBase();
    setLayout(base);
    setDirty(false);
    toast.success(base.source === "role" ? "Reset to your role starter dashboard"
      : base.source === "org" ? "Reset to the organisation default" : "Reset to Nirvana Default");
  };

  const activeTypes = useMemo(
    () => Object.entries(layout?.widgets || {}).filter(([, w]) => !w.hidden).map(([id]) => id),
    [layout]
  );
  const configInstance = configId ? layout.widgets[configId] : null;

  if (!layout) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-muted-foreground" data-testid="dashboard-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your workspace…
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="owner-dashboard">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-[0.18em] text-accent">Owner Control Centre</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Namaste{greeting ? `, ${greeting}` : ""}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {editing ? "Drag, resize and configure your widgets. Changes are personal to you." : "Here is what needs your attention today."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && !editing && (
            <div className="flex items-center gap-2" data-testid="preview-role-wrap">
              <Select value={previewRole || "none"} onValueChange={(v) => loadRolePreview(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 w-56 gap-1.5" data-testid="preview-role-select">
                  <Eye className="h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Preview as role…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">My dashboard</SelectItem>
                  {roles.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {editing && (
            <>
              <Button variant="outline" className="gap-1.5" onClick={() => setLibOpen(true)} data-testid="add-widget-btn">
                <Plus className="h-4 w-4" /> Add widget
              </Button>
              {isAdmin && (
                <Button variant="outline" className="gap-1.5" onClick={() => setTplOpen(true)} data-testid="templates-btn">
                  <Layers className="h-4 w-4" /> Templates
                </Button>
              )}
              <Button variant="ghost" className="gap-1.5" onClick={resetDefault} data-testid="reset-dashboard">
                <RotateCcw className="h-4 w-4" /> Reset
              </Button>
            </>
          )}
          {editing ? (
            <Button className="gap-1.5" disabled={saving} onClick={async () => { await persist(layout); setEditing(false); }} data-testid="save-dashboard">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Done
            </Button>
          ) : (
            <Button variant="outline" className="gap-1.5" onClick={() => setEditing(true)} data-testid="customize-dashboard">
              <Pencil className="h-4 w-4" /> Customise
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-dashed border-accent/50 bg-accent-subtle/40 px-4 py-2 text-xs text-muted-foreground" data-testid="edit-hint">
          <span className="hidden items-center gap-1 sm:flex"><Monitor className="h-3.5 w-3.5" /> Desktop</span>
          <span className="flex items-center gap-1 sm:hidden"><Smartphone className="h-3.5 w-3.5" /> Mobile</span>
          layout — drag by the handle, use the ⋮ menu to resize, configure or hide. {dirty && <span className="font-medium text-accent">Unsaved changes</span>}
        </div>
      )}

      {previewRole && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/50 bg-accent-subtle/50 px-4 py-2.5 text-sm" data-testid="preview-role-banner">
          <span className="flex items-center gap-2 text-muted-foreground">
            <Eye className="h-4 w-4 text-accent" /> Previewing the <b className="text-foreground">{previewRole}</b> starter dashboard (read-only). Your own dashboard is unchanged.
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={saveRoleStarter} data-testid="save-role-starter">Save as {previewRole} starter</Button>
            <Button size="sm" variant="ghost" onClick={() => loadRolePreview("")} data-testid="exit-preview">Exit preview</Button>
          </div>
        </div>
      )}

      <DashboardGrid
        layout={layout}
        editing={editing}
        onLayoutChange={onLayoutChange}
        onCollapse={onCollapse}
        onHide={onHide}
        onResize={onResize}
        onConfigure={(id) => setConfigId(id)}
      />

      <WidgetLibrary open={libOpen} onOpenChange={setLibOpen} activeTypes={activeTypes} onAdd={addWidget} />
      <WidgetConfig open={!!configId} onOpenChange={(o) => !o && setConfigId(null)} instance={configInstance} onSave={onSaveConfig} />
      {isAdmin && <TemplateManager open={tplOpen} onOpenChange={setTplOpen} currentLayout={layout} />}
    </div>
  );
}
